import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { RefreshCw, ShieldCheck, ShieldAlert } from 'lucide-react';

export function MathCaptcha({ onVerify, className = '' }) {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [operator, setOperator] = useState('+');
  const [userAnswer, setUserAnswer] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');

  const generateCaptcha = () => {
    const operators = ['+', '-', '×'];
    const op = operators[Math.floor(Math.random() * operators.length)];
    let n1, n2;

    if (op === '+') {
      n1 = Math.floor(Math.random() * 20) + 1;
      n2 = Math.floor(Math.random() * 20) + 1;
    } else if (op === '-') {
      n1 = Math.floor(Math.random() * 20) + 10;
      n2 = Math.floor(Math.random() * 10) + 1;
      // Ensure n1 > n2 for positive result
      if (n1 < n2) [n1, n2] = [n2, n1];
    } else {
      n1 = Math.floor(Math.random() * 10) + 1;
      n2 = Math.floor(Math.random() * 10) + 1;
    }

    setNum1(n1);
    setNum2(n2);
    setOperator(op);
    setUserAnswer('');
    setIsVerified(false);
    setError('');
    onVerify(false);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const getCorrectAnswer = () => {
    switch (operator) {
      case '+':
        return num1 + num2;
      case '-':
        return num1 - num2;
      case '×':
        return num1 * num2;
      default:
        return num1 + num2;
    }
  };

  const handleVerify = () => {
    const correctAnswer = getCorrectAnswer();
    const userNum = parseInt(userAnswer, 10);

    if (isNaN(userNum)) {
      setError('Please enter a number');
      setIsVerified(false);
      onVerify(false);
      return;
    }

    if (userNum === correctAnswer) {
      setIsVerified(true);
      setError('');
      onVerify(true);
    } else {
      setError('Incorrect answer. Please try again.');
      setIsVerified(false);
      onVerify(false);
      // Generate new captcha after wrong answer
      setTimeout(() => {
        generateCaptcha();
      }, 1500);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleVerify();
    }
  };

  return (
    <div className={`bg-slate-50 border border-slate-200 rounded-xl p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        {isVerified ? (
          <ShieldCheck className="w-5 h-5 text-green-600" />
        ) : (
          <ShieldAlert className="w-5 h-5 text-slate-500" />
        )}
        <Label className="font-semibold text-slate-700">Security Verification</Label>
      </div>

      {isVerified ? (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <ShieldCheck className="w-5 h-5 text-green-600" />
          <span className="text-green-700 font-medium">Verified! You're human.</span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1">
              <Label className="text-sm text-slate-600 mb-1 block">
                Solve this math problem:
              </Label>
              <div className="flex items-center gap-2">
                <div className="bg-white border-2 border-primary/30 rounded-lg px-4 py-3 text-center min-w-[140px]">
                  <span className="text-xl font-bold text-slate-800">
                    {num1} {operator} {num2} = ?
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={generateCaptcha}
                  className="text-slate-500 hover:text-slate-700"
                  title="Get new problem"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Your answer"
              className="flex-1 text-center text-lg font-medium"
              data-testid="captcha-input"
            />
            <Button
              type="button"
              onClick={handleVerify}
              className="bg-primary hover:bg-primary/90"
              data-testid="captcha-verify-btn"
            >
              Verify
            </Button>
          </div>

          {error && (
            <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
              <ShieldAlert className="w-4 h-4" />
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default MathCaptcha;
